import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Dloria: the drawer no longer opens as the add starts. It opens once the new line has rendered,
    // so it never slides in empty or stale and then jumps; the add button's own animation covers the wait.
    const linesBefore = this.#lineQuantities();

    event.promise
      ?.then(({ cart, detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = async () => {
          await this.#whenShowingCount(cart?.totalQuantity);
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          this.#highlightChangedLines(linesBefore);
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Dloria: quantity per line key, to spot the line an add created or changed.
   * @returns {Map<string, number>}
   */
  #lineQuantities() {
    const lines = new Map();
    for (const row of this.querySelectorAll('tbody .cart-items__table-row[data-key]')) {
      const input = /** @type {HTMLInputElement | null} */ (row.querySelector('.quantity-selector input'));
      lines.set(/** @type {HTMLElement} */ (row).dataset.key ?? '', Number(input?.value) || 0);
    }
    return lines;
  }

  /**
   * Dloria: resolves once the drawer's item count shows `count` (the add has rendered), or after a
   * short timeout so a slow or failed render never keeps the drawer from opening.
   * @param {number | undefined} count
   * @returns {Promise<void>}
   */
  #whenShowingCount(count) {
    const showing = () =>
      count == null || this.querySelector('.theme-drawer__badge')?.textContent?.trim() === String(count);
    if (showing()) return Promise.resolve();

    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (!showing()) return;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      });
      const timer = setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 2500);
      observer.observe(this, { childList: true, subtree: true, characterData: true });
    });
  }

  /**
   * Dloria: tints the line that was just added (a new line or a higher quantity) so the eye finds it.
   * @param {Map<string, number>} linesBefore
   */
  #highlightChangedLines(linesBefore) {
    for (const row of this.querySelectorAll('tbody .cart-items__table-row[data-key]')) {
      const key = /** @type {HTMLElement} */ (row).dataset.key ?? '';
      const input = /** @type {HTMLInputElement | null} */ (row.querySelector('.quantity-selector input'));
      if (linesBefore.has(key) && (Number(input?.value) || 0) <= (linesBefore.get(key) ?? 0)) continue;
      row.classList.add('cart-items__table-row--added');
      // The tint animates on the row's ::before, which only a subtree lookup includes
      onAnimationEnd(row, () => row.classList.remove('cart-items__table-row--added'));
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
